import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { MenuItem } from '@/mocks/menu';
import { dietaryFilters, categories } from '@/mocks/menu';

interface MenuDetailModalProps {
  item: MenuItem | null;
  onClose: () => void;
}

export default function MenuDetailModal({ item, onClose }: MenuDetailModalProps) {
  const { t, i18n } = useTranslation();
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  const isRTL = i18n.language === 'ar';

  const categoryInfo = categories.find((c) => c.id === item?.category);

  // Lock body scroll when modal opens
  useEffect(() => {
    if (item) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [item]);

  if (!item) return null;

  const tags = dietaryFilters.filter((f) => item.tags?.includes(f.id));

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-center p-3 md:p-6 pt-8 md:pt-16"
      role="dialog"
      aria-modal="true"
      onClick={handleBackdropClick}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-foreground-950/50 backdrop-blur-sm animate-fade-in" />

      {/* Modal Card */}
      <div className="relative w-full max-w-2xl bg-background-50 rounded-2xl overflow-hidden shadow-2xl animate-modal-pop h-auto max-h-[85vh] flex flex-col md:flex-row">
        {/* Close button */}
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 end-3 z-30 w-9 h-9 flex items-center justify-center rounded-full bg-foreground-950/30 backdrop-blur-md text-background-50 hover:bg-foreground-950/50 transition-colors cursor-pointer"
        >
          <i className="ri-close-line text-lg" />
        </button>

        {/* Image */}
        <div className="relative w-full md:w-[42%] flex-shrink-0 bg-background-100">
          <div className="w-full aspect-[16/10] md:aspect-auto md:min-h-[320px] md:h-full">
            {!imageLoaded && !imageError && (
              <div className="absolute inset-0 flex items-center justify-center z-10">
                <div className="w-8 h-8 border-2 border-primary-300 border-t-transparent rounded-full animate-spin" />
              </div>
            )}
            {!imageError && (
              <img
                src={item.image}
                alt={t(`item.${item.id}.name`)}
                className={`w-full h-full object-cover object-center transition-opacity duration-300 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            )}
            {imageError && (
              <div className="absolute inset-0 flex items-center justify-center bg-background-100">
                <span className="w-14 h-14 flex items-center justify-center text-secondary-400">
                  <i className="ri-image-line text-3xl" />
                </span>
              </div>
            )}
          </div>

          {item.popular && (
            <div className="absolute top-3 start-3 z-20">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-accent-500 text-background-50 text-xs font-semibold rounded-full shadow-sm">
                <i className="ri-fire-line" />
                {t('menu.popular')}
              </span>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-1 overflow-y-auto p-5 md:p-6">
            {/* Category badge */}
            {categoryInfo && (
              <div className="mb-3">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-secondary-100 text-secondary-800 text-xs font-medium rounded-md">
                  <i className={`${categoryInfo.icon} text-xs`} />
                  {t(categoryInfo.nameKey)}
                </span>
              </div>
            )}

            {/* Title + Price */}
            <div className="flex items-start justify-between gap-3 mb-3 pe-8">
              <h3 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 leading-snug">
                {t(`item.${item.id}.name`)}
              </h3>
              <span className="text-primary-600 font-bold text-xl md:text-2xl whitespace-nowrap flex-shrink-0 mt-0.5">
                {isRTL ? `${item.price} ${t('menu.currency')}` : `${t('menu.currency')} ${item.price}`}
              </span>
            </div>

            {/* Description */}
            <p className="text-foreground-600 text-sm md:text-base leading-relaxed mb-5">
              {t(`item.${item.id}.desc`)}
            </p>

            {/* Dietary Tags */}
            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {tags.map((filter) => (
                  <span
                    key={filter.id}
                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-900 text-xs font-medium"
                  >
                    <span className="w-5 h-5 flex items-center justify-center rounded-md bg-background-50 border border-secondary-200 text-[10px] font-bold">
                      {filter.badge}
                    </span>
                    {t(filter.nameKey)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}